import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { message } from 'antd'
import { BuildingConfig, BuildingImage } from 'config/constants/types'
import type { BuildingsListingsState, BuildingsListing, BuildingsListingCheck } from '../types'
import { getBuildingListings, getBuilding } from './helpers'

const noBuildingsListingsConfig = {
  listings: [],
  isInitalized: false,
  isLoading: true,
  hasError: false,
  building: {
    images: [],
  },
  buildingIsLoading: true,
  buildingIsInitialized: false,
}

interface SetListingsPayload {
  listings: BuildingsListing[]
}

interface SetBuildingPayload {
  building: BuildingConfig
}

interface UpdateListingChecksPayload {
  checks: BuildingsListingCheck[]
}

interface UpdateBuildingImagesPayload {
  images: BuildingImage[]
}

const initialState: BuildingsListingsState = { ...noBuildingsListingsConfig }

export const buildingsListingsSlice = createSlice({
  name: 'BuildingsListings',
  initialState,
  reducers: {
    fetchListings: (state) => {
      state.isLoading = true
      state.hasError = false
    },
    setListings: (state, action: PayloadAction<SetListingsPayload>) => {
      state.listings = action.payload.listings
      state.isLoading = false
      state.isInitalized = true
      state.hasError = false
    },
    fetchListingsFailed: (state) => {
      state.listings = []
      state.isInitalized = false
      state.isLoading = false
      state.hasError = true
    },
    fetchBuilding: (state) => {
      state.buildingIsLoading = true
    },
    setBuilding: (state, action: PayloadAction<SetBuildingPayload>) => {
      state.building = action.payload.building
      state.buildingIsLoading = false
      state.buildingIsInitialized = true
    },
    updateListingChecks: (state, action: PayloadAction<UpdateListingChecksPayload>) => {
      const updatedListings = state.listings.map((listing) => {
        const check = action.payload.checks.find(
          (checkItem) => checkItem.listingId === listing._id || checkItem.buildingListingId === listing._id,
        )
        if (check) {
          let checks = []
          const userCheck = listing.checks?.find((checkItem) => checkItem.user === check.user)
          if (userCheck) {
            checks = listing.checks.map((checkItem) => {
              if (checkItem.user === check.user) {
                return check
              }
              return checkItem
            })
          } else {
            checks = listing.checks ? [check, ...listing.checks] : [check]
          }
          return {
            ...listing,
            status: check,
            checked: true,
            checks,
          }
        }
        return listing
      })

      state.listings = updatedListings
    },
    updateBuildingImages: (state, action: PayloadAction<UpdateBuildingImagesPayload>) => {
      state.building.images = action.payload.images.sort((a, b) => a.position - b.position)
    },
  },
})

export const fetchBuildingListings = (buildingId: string) => async (dispatch) => {
  try {
    dispatch(fetchListings())
    const listings = await getBuildingListings(buildingId)
    dispatch(
      setListings({
        listings,
      }),
    )
  } catch (error) {
    message.error('Error gettings listings')
    dispatch(fetchListingsFailed())
  }
}

export const fetchBuildingBuilding = (buildingId: string) => async (dispatch) => {
  try {
    dispatch(fetchBuilding())
    const building = await getBuilding(buildingId)
    dispatch(
      setBuilding({
        building,
      }),
    )
  } catch (error) {
    message.error('Error getting building')
  }
}

// Actions
export const {
  fetchListings,
  setListings,
  fetchListingsFailed,
  fetchBuilding,
  setBuilding,
  updateListingChecks,
  updateBuildingImages,
} = buildingsListingsSlice.actions

export default buildingsListingsSlice.reducer
